import { useEffect, useMemo, useState } from 'react';
import { DetailedShipList } from '@ais-ui/api-spec/config';
import { shiplistCallbacks } from '../../../utils/mutateCallbacks';
import { isPresent, isValidIMONumber, isValidMMSINumber } from '@ais-ui/utils';
import { ShiplistMutateFormValues } from './formTypes';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../../../Settings/SettingsProvider';
import { useSaveShiplistSettings } from '../../useSaveShiplistSettings';
import { useMutateShiplistContext } from './MutateShiplistContext';
import { ValidationIndices } from './ShiplistConfirmationDialog';

const getFirstInvalidIndex = (
  invalidIndices: ValidationIndices
): number | null => {
  const allIndices = [...invalidIndices.imo, ...invalidIndices.mmsi];
  if (allIndices.length === 0) return null;
  return Math.min(...allIndices);
};

export const useSaveShiplist = () => {
  const {
    setIsSubmitting,
    setShowDialog,
    mutate,
    action,
    shiplist,
    invalidIndices,
    itemsPerPage,
    goToPage,
    setInvalidIndices,
  } = useMutateShiplistContext();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { toast } = useToast();
  const { messages } = useLocalization();
  const { settings } = useSettings();

  const { saveShiplistSettings } = useSaveShiplistSettings();
  const [formData, setFormData] = useState<ShiplistMutateFormValues | null>(
    null
  );

  const shownShiplistsId = useMemo(() => {
    return settings?.['ribbonPlugin.settings']?.legend_shiplists || [];
  }, [settings]);

  const handleConfirm = () => {
    if (formData) {
      submitShiplist(formData);
    }
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setFormData(null);
    const firstInvalidIndex = getFirstInvalidIndex(invalidIndices);
    if (isPresent(firstInvalidIndex)) {
      const targetPage = Math.floor(firstInvalidIndex / itemsPerPage) + 1;
      goToPage(targetPage);
    }
  };

  const submitShiplist = (data: ShiplistMutateFormValues) => {
    setIsSubmitting(true);
    const shipListToSave: DetailedShipList = {
      name: data.name,
      max: 0,
      color: data.color.replace('#', ''),
      acl: data.selectedGroups.map((group) => group.label),
      id: shiplist?.id ?? '',
      ships: data.ships.map((ship) => ({
        name: ship?.name ?? '',
        mmsi: ship?.mmsi ? ship.mmsi : -1,
        imo: (ship?.imo ?? '') as number,
      })),
    };

    const extendedCallbacks = shiplistCallbacks(
      toast,
      messages,
      action,
      setIsSubmitting,
      setShowDialog
    );

    if (action === 'edit') {
      const shiplistName = shiplist?.name ? shiplist?.name : 'unknown shiplist';
      const id = shiplist?.id;
      if (!id) {
        toast({
          className: 'save-shiplist-error-toast',
          title: 'Error!',
          description: messages.shiplistsSaveFailedToEdit.replace(
            '{label}',
            shiplistName
          ),
          variant: 'destructive',
        });
        setShowDialog(false);
        return;
      }
      const originalIsApplied = shownShiplistsId.includes(shiplist?.id);
      if (originalIsApplied) {
        const newShownShiplistsId = shownShiplistsId.filter(
          (shiplistId: string) => shiplistId !== id
        );

        saveShiplistSettings(newShownShiplistsId, originalIsApplied);
      }
    }

    mutate(shipListToSave, extendedCallbacks);
  };

  const onSubmit = (data: ShiplistMutateFormValues) => {
    // Check all IMOs and MMSIs
    const invalidIMOIndices = data.ships
      .map((ship, index) => ({
        imo: ship?.imo,
        index,
      }))
      .filter(({ imo }) => imo && !isValidIMONumber(imo.toString()))
      .map(({ index }) => index);

    const invalidMMSIIndices = data.ships
      .map((ship, index) => ({
        mmsi: ship?.mmsi,
        index,
      }))
      .filter(({ mmsi }) => mmsi && !isValidMMSINumber(mmsi.toString()))
      .map(({ index }) => index);

    setFormData(data);

    // If there are any invalid numbers, show confirmation dialog
    if (invalidIMOIndices.length > 0 || invalidMMSIIndices.length > 0) {
      setShowConfirmDialog(true);
      setInvalidIndices({
        imo: invalidIMOIndices,
        mmsi: invalidMMSIIndices,
      });
      return;
    }

    submitShiplist(data);
  };

  return {
    onSubmit,
    handleCancel,
    handleConfirm,
    showConfirmDialog,
    setShowConfirmDialog,
  };
};
